/*
  Refill & Replacement Parts Finder.

  Product commerce data is server-rendered from Shopify Product references in
  Liquid. This file is UI-only: filtering, chip navigation, identification
  zoom, and best-value labeling. It intentionally contains no currency or
  product-fetching logic.
*/
(() => {
  const root = document.querySelector('[data-rf]');
  if (!root) return;

  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------- lookups

  const typeInputs = root.querySelectorAll('[data-rf-type]');
  const rangeChoices = root.querySelectorAll('[data-rf-range-choice]');
  const rangeInputs = root.querySelectorAll('[data-rf-range]');
  const results = root.querySelector('[data-rf-results]');
  const resultsHeading = root.querySelector('[data-rf-results-heading]');
  const families = root.querySelectorAll('[data-rf-family][data-rf-key]');
  const stepOne = root.querySelector('[data-rf-step="1"]');
  const stepTwo = root.querySelector('[data-rf-step-two]');
  const empty = root.querySelector('[data-rf-empty]');

  const mappingNodes = root.querySelectorAll('[data-rf-map-family]');
  const rangeTypes = new Map();
  const familyMappings = new Map();

  mappingNodes.forEach((node) => {
    const range = node.dataset.rfMapRange;
    const type = node.dataset.rfMapType;
    const family = node.dataset.rfMapFamily;
    if (!range || !type || !family) return;

    if (!rangeTypes.has(range)) rangeTypes.set(range, new Set());
    rangeTypes.get(range).add(type);

    const key = `${range}-${type}`;
    if (!familyMappings.has(key)) familyMappings.set(key, new Set());
    familyMappings.get(key).add(family);
  });

  const state = { type: null, range: null };

  results.hidden = true;
  families.forEach((panel) => {
    panel.hidden = true;
  });

  function showRangesForType() {
    let visible = 0;
    rangeChoices.forEach((choice) => {
      const range = choice.querySelector('[data-rf-range]')?.value;
      const ok = Boolean(range && rangeTypes.get(range)?.has(state.type));
      choice.hidden = !ok;
      if (ok) visible += 1;
      const input = choice.querySelector('input');
      if (!ok && input?.checked) {
        input.checked = false;
        state.range = null;
      }
    });
    if (stepTwo) stepTwo.hidden = visible === 0;
  }

  function markSteps() {
    if (stepOne) stepOne.dataset.rfDone = state.type ? 'true' : 'false';
    if (stepTwo) stepTwo.dataset.rfDone = state.range ? 'true' : 'false';
  }

  function render() {
    markSteps();

    if (!state.type || !state.range) {
      results.hidden = true;
      return;
    }

    const mappingKey = `${state.range}-${state.type}`;
    const matchingFamilies = familyMappings.get(mappingKey) || new Set();
    let shown = 0;
    families.forEach((panel) => {
      const ok = matchingFamilies.has(panel.dataset.rfKey);
      panel.hidden = !ok;
      if (ok) shown += 1;
    });

    results.hidden = false;
    if (empty) empty.hidden = shown > 0;

    if (resultsHeading) {
      const rangeName = root.querySelector(`[data-rf-range][value="${state.range}"]`)
        ?.dataset.rfRangeName;
      const typeName = root.querySelector(`[data-rf-type][value="${state.type}"]`)?.dataset
        .rfTypeName;
      const what = [rangeName, typeName].filter(Boolean).join(' ');
      resultsHeading.textContent = shown
        ? `What fits your ${what}`
        : 'Nothing listed for that combination';
    }
  }

  function jumpToStepTwo() {
    if (!stepTwo || stepTwo.hidden) return;
    requestAnimationFrame(() => stepTwo.scrollIntoView({ block: 'start', behavior: 'auto' }));
  }

  function jumpToVisibleCards() {
    const panel = [...families].find((family) => !family.hidden);
    const cards = panel?.querySelector('.rf-cards');
    if (!cards) return;
    requestAnimationFrame(() => cards.scrollIntoView({ block: 'start', behavior: 'auto' }));
  }

  typeInputs.forEach((input) =>
    input.addEventListener('change', () => {
      state.type = input.value;
      showRangesForType();
      render();
      jumpToStepTwo();
    })
  );

  rangeInputs.forEach((input) =>
    input.addEventListener('change', () => {
      state.range = input.value;
      render();
      jumpToVisibleCards();
    })
  );

  // ------------------------------------------------------- chip navigation

  root.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-rf-chip]');
    if (!chip) return;

    const rangeKey = chip.dataset.rfChipRange;
    const familyKey = chip.dataset.rfChipFamily;
    const type = chip.dataset.rfChipType;
    const panel = root.querySelector(`[data-rf-family][data-rf-key="${familyKey}"]`);
    const rangeInput = root.querySelector(`[data-rf-range][value="${rangeKey}"]`);
    const typeInput = root.querySelector(`[data-rf-type][value="${type}"]`);
    if (!panel || !rangeInput || !typeInput) return;

    event.preventDefault();

    typeInput.checked = true;
    state.type = type;
    showRangesForType();
    rangeInput.checked = true;
    state.range = rangeKey;
    render();

    panel.scrollIntoView({ behavior: motionOk ? 'smooth' : 'auto', block: 'start' });
    const heading = panel.querySelector('h4');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  });

  // ------------------------------------------------ identification navigation

  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-rf-goto-family]');
    if (!link) return;

    const familyKey = link.dataset.rfGotoFamily;
    const panel = root.querySelector(`[data-rf-family][data-rf-key="${familyKey}"]`);
    if (!panel) return;

    event.preventDefault();

    typeInputs.forEach((input) => {
      input.checked = false;
    });
    rangeInputs.forEach((input) => {
      input.checked = false;
    });
    state.type = null;
    state.range = null;
    showRangesForType();
    markSteps();

    families.forEach((other) => {
      other.hidden = other !== panel;
    });
    results.hidden = false;
    if (empty) empty.hidden = true;
    if (resultsHeading) {
      const name = link.dataset.rfGotoName;
      resultsHeading.textContent = name ? `Every ${name} we stock` : 'Every refill we stock';
    }

    panel.scrollIntoView({ behavior: motionOk ? 'smooth' : 'auto', block: 'start' });
    const target = panel.querySelector('h4');
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  });

  // ----------------------------------------------------- identification zoom

  const zoomDialog = root.querySelector('[data-rf-zoom-dialog]');
  if (zoomDialog && typeof zoomDialog.showModal === 'function') {
    const zoomImg = zoomDialog.querySelector('[data-rf-zoom-img]');
    const zoomCaption = zoomDialog.querySelector('[data-rf-zoom-caption]');

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-rf-zoom]');
      if (!trigger) return;

      event.preventDefault();
      const name = trigger.dataset.rfZoomName || '';
      zoomImg.removeAttribute('src');
      zoomImg.src = trigger.dataset.rfZoomSrc || '';
      zoomImg.alt = name ? `${name}, enlarged` : '';
      if (zoomCaption) zoomCaption.textContent = name;
      zoomDialog.showModal();
    });

    zoomDialog.addEventListener('click', (event) => {
      if (event.target === zoomDialog || event.target.closest('[data-rf-zoom-close]')) {
        zoomDialog.close();
      }
    });
  }

  // ---------------------------------------------------------- best value

  function nudgeBestValue(panel) {
    const groups = new Map();
    const cards = [...panel.querySelectorAll('[data-rf-card]')];

    cards.forEach((card) => {
      if (!card.dataset.rfUnit || !card.dataset.rfGroup) return;
      groups.set(card.dataset.rfGroup, (groups.get(card.dataset.rfGroup) || []).concat(card));
    });

    groups.forEach((groupCards) => {
      if (groupCards.length < 2) return;

      const values = groupCards.map((card) => Number(card.dataset.rfUnit));
      const bestValue = Math.min(...values);
      if (values.every((value) => value === bestValue)) return;

      const best = groupCards.find((card) => Number(card.dataset.rfUnit) === bestValue);
      if (!best || best.querySelector('[data-rf-price] .rf-card__best')) return;

      const flag = document.createElement('span');
      flag.className = 'rf-card__best';
      flag.textContent = 'Best value per refill';
      best.querySelector('[data-rf-price]')?.append(flag);
    });
  }

  families.forEach(nudgeBestValue);

  markSteps();
  root.dataset.rfEnhanced = 'true';
})();